//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/core/services/service';

/**
 * @service TreatmentLogService
 * @description Service for managing Treatment Logs within the aquaculture system.
 * Treatment logs record the application of various inputs to ponds, such as:
 * - **Medicines**: Antibiotics or treatments for diseases.
 * - **Probiotics**: Beneficial bacteria for water quality.
 * - **Chemicals**: Lime, disinfectants, etc.
 * - **Feed Additives**: Supplements mixed with feed.
 *
 * This service provides the interface to retrieve these records from the backend.
 * It interacts with the `/blubay/treatmentlog/` backend endpoint.
 *
 * **Key Responsibilities:**
 * 1. **Log Retrieval**: Fetching historical treatment records.
 * 2. **Daily Activity**: Retrieving treatments scheduled or performed "today".
 * 3. **Batch Tracking**: Fetching treatments associated with specific batch entries (e.g., bulk application across a section).
 * 4. **Resource Management**: Handling HTTP request lifecycles to prevent memory leaks.
 *
 * @feature 1. Retrieve all treatment activities.
 * @feature 2. Get today's treatments for specific farm sites.
 * @feature 3. Retrieve treatments by batch number.
 * @feature 4. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<TreatmentLogModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /blubay/treatmentlog/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class TreatmentLogService extends Service<any> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/blubay/treatmentlog/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/treatmentlog/');
  }

  /**
   * @method getAllTreatmentLogActivities
   * @async
   * @description Fetch all treatment log activities from the backend.
   *
   * This method performs a GET request to the service endpoint. It is configured to retrieve
   * a large number of records (pageSize=20000) to ensure a comprehensive history is loaded,
   * ordered by creation date ('new').
   *
   * @returns {Promise<Array<TreatmentLogModel>>} Promise resolving to an array of TreatmentLogModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * treatmentLogService.getAllTreatmentLogActivities().then(logs => {
   *   console.log('Treatment History:', logs);
   * });
   */
  public getAllTreatmentLogActivities() {
    return new Promise<Array<any>>((resolve, reject) => {
      const request = this.httpClient.get<Array<any>>(`${this.baseUrl}${this.serviceUrl}?pageSize=20000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
